import {createAsyncThunk} from '@reduxjs/toolkit'
import {LoginRequest, MeRequest, ForgottenPasswordRequest, ResetPasswordRequest} from './types'
import axios, {AxiosError} from 'axios'
import {loginUrl, meUrl, requestForgottenPasswordUrl, resetPasswordUrl} from '../../api'
import {User} from '../../../models'

// Request deduplication - prevent the same request from being sent multiple times
const pendingRequests = new Map<string, Promise<any>>()

function deduplicate<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key)!
  }

  const promise = requestFn().finally(() => {
    pendingRequests.delete(key)
  })
  
  pendingRequests.set(key, promise)
  return promise
}

export const login = createAsyncThunk(
  'auth/login',
  async (payload: LoginRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post<{user: User; token: string}>(loginUrl, payload)
      const {user, token} = res.data
      return {
        success: true,
        user,
        token,
      }
    } catch (error) {
      const err = error as AxiosError<{en?: string; fr?: string; message?: string}>
      // Extract error message from response (try en, then fr, then message, then default)
      const errorMessage = err.response?.data?.en || err.response?.data?.fr || err.response?.data?.message || err.message || 'Login failed'
      
      if (process.env.NODE_ENV === 'development') {
        console.error('[Login] Error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: errorMessage,
          url: loginUrl,
        })
      }
      
      return rejectWithValue({
        success: false,
        message: errorMessage,
        status: err.response?.status,
      })
    }
  }
)

export const getMe = createAsyncThunk(
  'auth/getMe',
  async (payload: MeRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get<{user: User}>(meUrl, payload)
      const {user} = res.data
      return {
        success: true,
        user,
      }
    } catch (error) {
      const err: any = error as AxiosError
      return rejectWithValue({success: false, message: err.response?.data.fr})
    }
  }
)

export const requestForgottenPassword = createAsyncThunk(
  'auth/requestForgottenPassword',
  async (payload: ForgottenPasswordRequest, {rejectWithValue}) => {
    const requestKey = `forgot-password:${payload.username}`
    
    try {
      const res = await deduplicate(requestKey, () => 
        axios.post(requestForgottenPasswordUrl, payload)
      )
      const {user} = res.data
      return {
        success: true,
        user,
      }
    } catch (error) {
      const err: any = error as AxiosError
      return rejectWithValue({success: false, message: err.response?.data.fr})
    }
  }
)

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (payload: ResetPasswordRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(resetPasswordUrl, payload)
      const {user} = res.data
      return {
        success: true,
        user,
      }
    } catch (error) {
      const err: any = error as AxiosError
      return rejectWithValue({success: false, message: err.response?.data.fr})
    }
  }
)
